import CheckMarkIcon from "@assets/icons/checkMarkIcon";
import CollapseArrowIcon from "@assets/icons/collapseArrowIcon";
import EditUserIcon from "@assets/icons/editUserIcon";
import { PolicyEnum } from "@config/policyEnum";
import { useHasPolicy } from "@config/useHasPolicy";
import {
  Box,
  Card,
  FormControl,
  MenuItem,
  Select,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useParams } from "react-router";
interface UserBasicInformationProps {
  handleChange: (
    event: React.ChangeEvent<HTMLInputElement | { value: string; name: string }>
  ) => void;
  errors: any;
  userData: any;
  updateUserInfo: boolean;
  editUserDetails: boolean;
  setUpdateUserInfo: React.Dispatch<React.SetStateAction<boolean>>;
}

const UserBasicInformation: React.FC<UserBasicInformationProps> = ({
  handleChange,
  errors,
  userData,
  updateUserInfo,
  editUserDetails,
  setUpdateUserInfo,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [showBasicInformation, setShowBasicInformation] = useState(true);
  const { securityGroups } = useSelector((state: any) => state.securityGroups);
  const { id } = useParams();
  const location = useLocation();
  const { user } = useSelector((state: any) => state.user);
  const getUserInfoPath = location.pathname.startsWith("/userInfo");
  
  const showFirstname = useHasPolicy(getUserInfoPath ? PolicyEnum.VIEW_PROFILE_FIRST_NAME : PolicyEnum.VIEW_USER_FIRST_NAME);
  const showLastname = useHasPolicy(getUserInfoPath ? PolicyEnum.VIEW_PROFILE_LAST_NAME : PolicyEnum.VIEW_USER_LAST_NAME);
  const showAssignSecurityGroup = useHasPolicy(getUserInfoPath ? PolicyEnum.VIEW_PROFILE_ASSIGN_SECURITY : PolicyEnum.VIEW_USER_ASSIGN_SECURITY_GROUP);
  const editFirstname = useHasPolicy(getUserInfoPath ? PolicyEnum.EDIT_PROFILE_FIRST_NAME : PolicyEnum.EDIT_USER_FIRST_NAME);
  const editLastname = useHasPolicy(getUserInfoPath ? PolicyEnum.EDIT_PROFILE_LAST_NAME : PolicyEnum.EDIT_USER_LAST_NAME);
  const editAssignSecurityGroup = useHasPolicy(getUserInfoPath ? false : PolicyEnum.EDIT_USER_ASSIGN_SECURITY_GROUP);
  const isDisabled = id ? !updateUserInfo : false;
  const disableFirstname = id ? !editFirstname : false;
  const disableLastname = id ? !editLastname : false;
  const disableSecurityGroup = id ? (!updateUserInfo || !editAssignSecurityGroup) : false;

  const handleCollapse = () => setShowBasicInformation(!showBasicInformation);

  const getSecurityGroupRenderValue = (selected: any) => {
    if (!selected) {
      if (securityGroups.length > 0 && (userData.securityGroupId || getUserInfoPath)) {
        const group = securityGroups.find((group: any) => group.id === userData.securityGroupId);
        const groupName = group ? group.groupName : (
          <Typography sx={{ color: "#0000005D" }}>Select Security Group</Typography>
        );
        return getUserInfoPath ? user.securityGroupName : groupName;
      } else {
        return <Typography sx={{ color: "#0000005D" }}>Select Security Group</Typography>;
      }
    }
    const group = securityGroups.find((group: any) => group.id === userData.securityGroupId);
    return group ? group.groupName : selected;
  };

  const renderFirstNameField = () => (
    <Box className="flex flex-col ">
      <Typography variant="body1" className="user-form-lbl">
        First Name <span style={{ color: "red" }}>*</span>
      </Typography>
      <TextField
        required
        fullWidth
        name="firstName"
        placeholder="Enter First Name"
        value={userData.firstName}
        size="small"
        onChange={handleChange}
        error={!!errors.firstName}
        helperText={errors.firstName}
        disabled={isDisabled || disableFirstname}
      />
    </Box>
  );

  const renderLastNameField = () => (
    <Box className="flex flex-col">
      <Typography variant="body1" className="user-form-lbl">
        Last Name <span style={{ color: "red" }}>*</span>
      </Typography>
      <TextField
        required
        fullWidth
        name="lastName"
        placeholder="Enter Last Name"
        size="small"
        value={userData.lastName}
        onChange={handleChange}
        error={!!errors.lastName}
        helperText={errors.lastName}
        disabled={isDisabled || disableLastname}
      />
    </Box>
  );

  const renderSecurityGroupField = () => (
    <Box className="flex flex-col">
      <Typography variant="body1" className="user-form-lbl">
        Assign Security Group <span style={{ color: "red" }}>*</span>
      </Typography>
      <FormControl required fullWidth size="small">
        <Select
          defaultValue=""
          name="securityGroupId"
          aria-label="securityGroup"
          onChange={handleChange}
          displayEmpty
          disabled={disableSecurityGroup}
          MenuProps={{PaperProps: {style: {maxHeight: 250, overflowY: 'auto'}}}}
          renderValue={getSecurityGroupRenderValue}
        >
          {securityGroups
            ?.filter((item: any) => item.groupName !== "Organization Admin")
            ?.map((group: any) => (
              <MenuItem key={group.id} value={group.id}>{group.groupName}</MenuItem>
            ))}
        </Select>
        <Typography variant="caption" color="error">{errors.securityGroupId}</Typography>
      </FormControl>
    </Box>
  );

  return (
    <>
      {(showFirstname || showLastname || showAssignSecurityGroup) && (
        <Card className="pt-2 md:!shadow-none">
          <Box className="hidden md:flex md:items-center bg-[#F2F2F2]"  sx={{ ml: '5px' }}>
            <Typography
              sx={{
                padding: "0.5rem 0.5rem 0.5rem 1rem",
                fontWeight: 500,
                fontSize:"15px"
              }}
            >
              Basic Information
            </Typography>
          </Box>
          <Box className="flex justify-between items-center border-b-2 md:hidden">
            <Box className="flex items-center">
              <Typography
                sx={{
                  padding: "0.5rem 0.5rem 0.5rem 1rem",
                  backgroundColor: { xs: "#fff", sm: "#F2F2F2" },
                  fontWeight: 400,
                  fontSize:"16px"
                }}
              >
                Basic Information
              </Typography>
              {!getUserInfoPath && <CheckMarkIcon />}
            </Box>
            <Box className="flex items-center">
              {editUserDetails && (
                <Typography onClick={() => setUpdateUserInfo(true)} className="cursor-pointer">
                  <EditUserIcon />
                </Typography>
              )}
              <Typography className="pr-3 pl-3 cursor-pointer" onClick={handleCollapse}>
                <CollapseArrowIcon />
              </Typography>
            </Box>
          </Box>

          {(showBasicInformation || !isMobile) && (
            <Box className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 p-4">
              {showFirstname && renderFirstNameField()}
              {showLastname && renderLastNameField()}
              {showAssignSecurityGroup && renderSecurityGroupField()}
            </Box>
          )}
        </Card>
      )}
    </>
  );
};

export default UserBasicInformation;
